import React from 'react';
import { Mui } from '@osu/react-ui';
import { 
  UnderReviewTemplate, 
  DecisionTemplate, 
  InvitationTemplate 
} from '../templates';
import { PreferencesFormWrapper, StepActions } from '../forms';
import StepContent from './StepContent';
import StepIcon from './StepIcon';
import StepStatusChip from './StepStatusChip';



/**
 * Enhanced VerticalTimelineStep Component for V2
 * Uses template-based rendering for different step types
 */
function VerticalTimelineStep({ 
  step, 
  stepIndex,
  isLast = false, 
  onAction,
  applicationData = {} 
}) {
  if (!step) {
    return null;
  }



  const handleAction = (actionType, actionData) => {
    if (onAction) {
      onAction(actionType, actionData);
    }
  };

  // Render step content based on template type
  const renderStepContent = () => {
    const { templateType, templateProps } = step;

    switch (templateType) {
      case 'under-review':
        return (
          <UnderReviewTemplate
            applicationData={applicationData}
            stepId={templateProps?.stepId || step.id}
            status={templateProps?.status || 'ACTIVE'}
            stepContent={step.content} // Pass pre-computed content
          />
        );

      case 'decision':
        return (
          <DecisionTemplate
            applicationData={applicationData}
            stepId={templateProps?.stepId || step.id}
            stepContent={step.content} // Pass pre-computed content
            stepActions={step.actions} // Pass pre-computed actions
            onAction={handleAction}
            // Note: DecisionTemplate will detect admin context internally
          />
        );

      case 'invitation':
        return (
          <InvitationTemplate
            applicationData={applicationData}
            stepId={templateProps?.stepId || step.id}
            status={templateProps?.status || 'INVITED'}
            stepContent={step.content} // Pass the pre-computed content
            stepActions={step.actions} // Pass the pre-computed actions
            onAction={handleAction}
          />
        );

      case 'preferences':
        // For preferences, we'll use the default StepContent for now
        return <StepContent step={step} onAction={handleAction} />;

      default:
        // Fallback to original StepContent for compatibility
        return <StepContent step={step} onAction={handleAction} />;
    }
  };

  return (
    <Mui.Box 
      data-step-id={step.id}
      data-step-index={stepIndex}
      sx={{ 
        display: "flex", 
        alignItems: "flex-start", 
        // has593v2 — tighter vertical gap between steps on mobile; sm+ keeps the original 3/6 spacing.
        mb: isLast ? 0 : step.showCondensed ? { xs: 2, sm: 3 } : { xs: 3, sm: 6 }
      }}
    >
      {/* Step Icon — hidden on phones (has593v2); the connector line is likewise hidden on xs in
          DashboardContent. The step content below stays and goes full-width on phones. */}
      <Mui.Box
        data-step-icon
        sx={{ display: { xs: 'none', sm: 'block' }, mr: { sm: 3 }, mt: { sm: 1 } }}
      >
        <StepIcon step={step} />
      </Mui.Box>

      {/* Step Content */}
      <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Step Title */}
        <Mui.Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            mb: { xs: 1, sm: 2 }, // has593v2 — tighter title spacing on mobile; sm+ unchanged
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: step.isActive ? 600 : step.showCondensed ? 500 : 400,
            color: step.isActive ? 'primary.main' : 
                   step.showCondensed ? 'success.main' : 
                   step.showGrayedOut ? 'text.disabled' : 'text.primary',
            opacity: step.showGrayedOut ? 0.6 : 1
          }}
        >
          {step.title}
        </Mui.Typography>

        {/* Step Content - Show different views based on step state */}
        {step.showCondensed ? (
          // Completed steps - minimal view (step icon already shows completion)
          <Mui.Box sx={{ opacity: 0.8 }}>
            {/* Step icon already indicates completion - no need for redundant chip */}
          </Mui.Box>
        ) : step.showGrayedOut ? (
          // Future/pending steps - grayed out view
          <Mui.Box sx={{ opacity: 0.5 }}>
            <Mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {step.content?.description || 'This step will be available once the previous steps are completed.'}
            </Mui.Typography>
            
            <Mui.Chip
              label="Pending"
              color="default"
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            />
          </Mui.Box>
        ) : step.status === 'EXTERNAL_COMPLETE' ? (
          // External complete - step icon already shows completion
          <>
            {renderStepContent()}
          </>
        ) : (
          // Active step - full content
          <>
            {/* Step Status Chip - shows "Action Available" when needed */}
            <StepStatusChip step={step} />
            {!step.showPreferencesForm && renderStepContent()}
            <PreferencesFormWrapper step={step} onAction={handleAction} applicationData={applicationData} />
            {/* Only render StepActions for templates that don't handle their own actions */}
            {!step.showPreferencesForm && !['invitation', 'decision'].includes(step.templateType) && (
              <StepActions step={step} onAction={handleAction} />
            )}
          </>
        )}
      </Mui.Box>
    </Mui.Box>
  );
}

export default VerticalTimelineStep;